/**
 * ReportColumnSelector
 *
 * Column picker for report view. Allows users to select which columns
 * to display in the report table.
 *
 * All field options are fetched from Frappe meta - nothing hardcoded.
 */

import { useState, useMemo } from "react";
import { useReportMeta } from "../../api/useReportMeta";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Columns3, Search } from "lucide-react";

interface ReportColumnSelectorProps {
  doctype: string;
  value: string[];
  onChange: (columns: string[]) => void;
  disabled?: boolean;
}

export function ReportColumnSelector({
  doctype,
  value,
  onChange,
  disabled,
}: ReportColumnSelectorProps) {
  const { data: meta, isLoading } = useReportMeta(doctype);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Use countable_fields as the list of all available columns
  const allFields = useMemo(() => {
    return meta?.countable_fields ?? [];
  }, [meta]);

  // Filter fields based on search query
  const filteredFields = useMemo(() => {
    if (!searchQuery.trim()) return allFields;
    const query = searchQuery.toLowerCase();
    return allFields.filter(
      (field) =>
        field.label.toLowerCase().includes(query) ||
        field.fieldname.toLowerCase().includes(query)
    );
  }, [allFields, searchQuery]);

  // Selected columns set for quick lookup
  const selectedSet = useMemo(() => new Set(value), [value]);

  const handleToggleColumn = (fieldname: string, checked: boolean) => {
    if (checked) {
      onChange([...value, fieldname]);
    } else {
      onChange(value.filter((f) => f !== fieldname));
    }
  };

  const handleSelectAll = () => {
    onChange(allFields.map((f) => f.fieldname));
  };

  const handleClearAll = () => {
    onChange([]);
  };

  if (isLoading) {
    return (
      <Button variant="outline" size="sm" disabled className="h-10">
        <Columns3 className="h-4 w-4 mr-2" />
        Loading...
      </Button>
    );
  }

  const selectedCount = value.length;
  const totalCount = allFields.length;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled || allFields.length === 0}
          className="h-10"
        >
          <Columns3 className="h-4 w-4 mr-2" />
          Columns
          {selectedCount > 0 && (
            <span className="ml-1 text-muted-foreground">
              ({selectedCount}/{totalCount})
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <div className="p-3 border-b">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search columns..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-8 border-0 shadow-none focus-visible:ring-0 px-0"
            />
          </div>
        </div>

        <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/30">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSelectAll}
            className="h-7 text-xs"
          >
            Select All
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearAll}
            className="h-7 text-xs"
          >
            Clear All
          </Button>
        </div>

        <ScrollArea className="h-64">
          <div className="p-2">
            {filteredFields.map((field) => (
              <label
                key={field.fieldname}
                className="flex items-center gap-3 px-2 py-1.5 rounded-md hover:bg-muted/50 cursor-pointer"
              >
                <Checkbox
                  checked={selectedSet.has(field.fieldname)}
                  onCheckedChange={(checked) =>
                    handleToggleColumn(field.fieldname, !!checked)
                  }
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{field.label}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {field.fieldname}
                  </div>
                </div>
              </label>
            ))}
            {filteredFields.length === 0 && (
              <div className="text-sm text-muted-foreground text-center py-4">
                No columns found
              </div>
            )}
          </div>
        </ScrollArea>

        <div className="p-2 border-t bg-muted/30">
          <Button
            onClick={() => setIsOpen(false)}
            className="w-full h-8 text-sm"
          >
            Done
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
